/**
 * What the party committees spent on one member — and on attacking them.
 *
 * This is the block where getting the categories wrong would do the most
 * damage, because it is the only place the money appears beside a person's
 * photograph. Elissa Slotkin's largest party committee figure is $16.9m and
 * the NRSC and NRCC spent it running advertisements against her; rendered
 * the way campaign money is rendered everywhere else on this site, her page
 * would report $16.9m of party support she never saw.
 *
 * So the two sides are separated before anything is drawn, the attacking
 * side leads with the words rather than the figure, and there is no total
 * across the two. There is a total *within* the attacking side, because two
 * committees buying advertising against the same person are doing the same
 * thing; there is deliberately none on the other side, where three different
 * acts would be added together.
 */

import { Link } from "react-router-dom";
import type { MemberCommitteeMoney } from "../api";
import { EFFECTS, UNCLASSIFIED_FACTS, figure, sideOf, spentAttacking } from "./committees";

export function CommitteeSpend({ rows, name }: { rows: MemberCommitteeMoney[]; name: string }) {
  if (rows.length === 0) return null;
  const sides = sideOf(rows);
  const attacked = spentAttacking(rows);

  return (
    <div className="mt-4 rounded-2xl border border-line bg-card p-6">
      <h3 className="text-sm font-semibold">From the party committees</h3>
      <p className="mt-1.5 text-sm text-muted">
        What the national and state party committees spent about {name} — which is not the same as
        what they gave them.{" "}
        <Link to="/money/committees" className="text-brand hover:underline">
          How these four categories work
        </Link>
        .
      </p>

      {sides.against.length > 0 && (
        <div className="mt-4 rounded-xl border border-gop/40 bg-gop/5 p-4">
          <p className="text-sm leading-relaxed">
            <span className="tnum font-display text-2xl font-semibold text-gop">
              {figure(attacked)}
            </span>{" "}
            <span className="font-medium">
              was spent on advertising attacking {name}, by{" "}
              {sides.against.length === 1 ? "one committee" : `${sides.against.length} committees`}.
            </span>{" "}
            <span className="text-muted">
              They never received it and none of it is part of what their campaign raised. The FEC
              files this spending under the name of the candidate it is aimed at, which is why it
              appears on their page rather than on their opponent's.
            </span>
          </p>
          <ul className="mt-3 space-y-1.5 border-t border-gop/20 pt-3">
            {sides.against.map((row) => (
              <Row key={`${row.committee_id}-${row.effect}`} row={row} />
            ))}
          </ul>
        </div>
      )}

      {sides.behind.length > 0 && (
        <div className="mt-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-faint">
            Spent on their side
          </p>
          <ul className="mt-2 space-y-1.5">
            {sides.behind.map((row) => (
              <Row key={`${row.committee_id}-${row.effect}`} row={row} />
            ))}
          </ul>
          <p className="mt-2 text-xs leading-relaxed text-faint">
            These are not added up. A cheque the campaign banked, money spent with the campaign
            under a limit of its own, and advertising bought without the campaign's involvement are
            three different acts, and one figure over the three would say none of them happened.
          </p>
        </div>
      )}

      {sides.unclassified.length > 0 && (
        <div className="mt-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-faint">
            Filed under a code this corpus will not classify
          </p>
          <ul className="mt-2 space-y-1.5">
            {sides.unclassified.map((row) => (
              <Row key={`${row.committee_id}-${row.effect}`} row={row} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** `{amount} {preposition} {category}, {committee}` — the direction between
 *  the figure and the name of whoever moved it, in reading order. */
function Row({ row }: { row: MemberCommitteeMoney }) {
  const facts = row.effect ? EFFECTS[row.effect] : UNCLASSIFIED_FACTS;
  const against = facts.direction === "against" && row.effect !== null;

  return (
    <li className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm">
      <span className={`tnum w-16 font-semibold ${against ? "text-gop" : ""}`}>
        {figure(row.amount)}
      </span>
      <span className={against ? "font-medium text-gop" : "text-muted"}>{facts.preposition}</span>
      <span className="min-w-0 flex-1">
        <Link
          to={`/money/committees/${row.committee_id}`}
          className="font-medium hover:text-brand"
        >
          {row.name ?? row.committee_id}
        </Link>
        <span className="ml-2 text-xs text-faint">{facts.label}</span>
      </span>
      <span className="text-xs text-faint">
        {row.transactions.toLocaleString()} {row.transactions === 1 ? "payment" : "payments"}
      </span>
    </li>
  );
}
